import { Breakpoint, ButtonType, UiComponentSize } from '@altibox/lte-salgskanaler-design-system-component-lib';
import React, { FC } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { useInitializeShoppingCart } from 'src/hooks/use-initialize-shopping-cart';
import { useSelectIsEligibleToPurchase } from 'src/hooks/use-select-is-eligible-to-purchase';
import { arrowLeftImageProps } from 'src/resources/images';
import { NavigationService } from 'src/services/navigation-service';
import { selectLastVisitedProductDetailsPage } from 'src/state/order/selectors';
import { useAppSelector } from 'src/state/thunk-api';
import { getBreadcrumbs, isFirstInternalBreadcrumb } from 'src/utils/breadcrumbs-utils';
import { useLargeTabletMediaQuery } from 'src/utils/breakpoints-utils';
import { isEligibleToUpgrade } from 'src/utils/equipment-utils';
import { i18n } from 'src/i18n';
import { useProductsResponseQueryStateOrThrow } from 'src/hooks/use-products-query';

import {
  StyledArrowLeftIcon,
  StyledBreadcrumb,
  StyledButton,
  StyledContainer,
  StyledExternalLink,
  StyledLink,
  StyledLinkContainer,
  StyledText,
} from './breadcrumb.styled';

export const Breadcrumb: FC = () => {
  const path = useLocation().pathname;
  const lastVisitedProductDetailsPage = useAppSelector(selectLastVisitedProductDetailsPage);
  const { upgradeDecoder } = useProductsResponseQueryStateOrThrow();
  const { isSuccess } = useInitializeShoppingCart();
  const eligibleToPurchase = useSelectIsEligibleToPurchase();
  const { externalBreadcrumbs, internalBreadcrumbs } = getBreadcrumbs(
    path,
    lastVisitedProductDetailsPage!,
    isEligibleToUpgrade(upgradeDecoder),
    eligibleToPurchase,
  );
  const navigate = useNavigate();

  const isOnLargeTablet = useLargeTabletMediaQuery();

  if (
    [NavigationService.SUCCESS].includes(path) ||
    (path === NavigationService.SUMMARY && !isSuccess) ||
    (isFirstInternalBreadcrumb(path, internalBreadcrumbs) && !isOnLargeTablet)
  ) {
    return null;
  }

  const handleBreadcrumbItemClick = () => navigate(-1);

  const renderMobileBreadcrumbItems = () => (
    <StyledButton
      buttonType={ButtonType.TERTIARY}
      uiComponentSize={UiComponentSize.MEDIUM}
      onClick={handleBreadcrumbItemClick}
      iconWrapper={{
        icon: <StyledArrowLeftIcon {...arrowLeftImageProps} />,
        placeIconBeforeText: true,
      }}
    >
      {i18n.component.breadcrumbs.backButton}
    </StyledButton>
  );

  const renderDesktopBreadcrumbItems = () => (
    <>
      {externalBreadcrumbs.map((breadcrumb, index) => (
        <StyledLinkContainer key={index}>
          <StyledExternalLink variant="paragraph1" maxBreakpoint={Breakpoint.MOBILE} href={breadcrumb.path}>
            {breadcrumb.text}
          </StyledExternalLink>
        </StyledLinkContainer>
      ))}
      {internalBreadcrumbs.map((breadcrumb, index) => (
        <StyledLinkContainer key={index}>
          {internalBreadcrumbs.length - 1 === index ? (
            <StyledText variant="paragraph1" component="p" maxBreakpoint={Breakpoint.MOBILE}>
              {breadcrumb.text}
            </StyledText>
          ) : (
            <StyledLink to={breadcrumb.path}>{breadcrumb.text}</StyledLink>
          )}
        </StyledLinkContainer>
      ))}
    </>
  );

  return (
    <StyledBreadcrumb>
      <StyledContainer>
        {isOnLargeTablet ? renderDesktopBreadcrumbItems() : renderMobileBreadcrumbItems()}
      </StyledContainer>
    </StyledBreadcrumb>
  );
};
